// FriendGroupsDock.js — Barre flottante « Mes groupes » (style Facebook), montée APP-WIDE.
// Accessible partout : liste tes groupes, chat compact, + COMPTEURS DE MESSAGES NON LUS par groupe
// (badge « nb de groupes avec non-lus » quand la barre est fermée) et AUTO-OUVERTURE de la discussion
// quand un membre rejoint/est actif dans un de tes groupes.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FiUsers, FiChevronUp, FiChevronDown, FiArrowLeft, FiX } from 'react-icons/fi';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';
import { GroupChat } from './GroupsPanel';

// Languette flottante « Mes groupes » — DESKTOP uniquement (sur mobile : regroupée dans le drawer social).
const Dock = styled.div`
  position:fixed;z-index:1499;font-family:${theme.fonts.body};
  display:none;                                        /* mobile : caché → dans le drawer social */
  @media (min-width:768px){ display:block; right:28rem; bottom:1.2rem; }   /* décalé (28rem) pour ne plus être chevauché par « Mes messages » (14.5rem) */
`;
// Mode « drawer » : section pleine largeur, toujours dépliée.
const Section = styled.div`font-family:${theme.fonts.body};`;
const SecHead = styled.div`display:flex;align-items:center;gap:.4rem;padding:.5rem .2rem;font-weight:700;font-size:.9rem;
  color:${theme.colors.textPrimary};border-bottom:1px solid ${theme.colors.border};`;
const Bar = styled.button`display:inline-flex;align-items:center;gap:.5rem;white-space:nowrap;cursor:pointer;position:relative;
  background:${theme.colors.gradient};color:#fff;border:none;border-radius:999px;
  padding:.6rem 1rem;font-weight:700;font-size:.86rem;box-shadow:${theme.shadows.card};
  .n{display:inline-flex;align-items:center;gap:.5rem;} .b{background:rgba(255,255,255,.25);border-radius:999px;padding:0 .45rem;font-size:.72rem;}
  .unb{position:absolute;top:-6px;right:-6px;min-width:18px;height:18px;padding:0 4px;border-radius:999px;background:#ef4444;color:#fff;
    font-size:.68rem;font-weight:800;display:inline-flex;align-items:center;justify-content:center;box-shadow:0 0 0 2px ${theme.colors.bgPrimary};}`;
const Panel = styled.div`position:absolute;bottom:calc(100% + .5rem);right:0;width:min(320px,80vw);
  background:${theme.colors.bgCard};border:1px solid ${theme.colors.borderHover};
  border-radius:14px;box-shadow:${theme.shadows.card};max-height:min(70vh,520px);overflow:hidden;display:flex;flex-direction:column;`;
const List = styled.div`overflow-y:auto;padding:.4rem;`;
const GItem = styled.button`width:100%;text-align:left;display:flex;align-items:center;gap:.6rem;background:none;border:none;cursor:pointer;
  color:${theme.colors.textPrimary};padding:.55rem .6rem;border-radius:${theme.radii.md};font-size:.88rem;
  &:hover{background:${theme.colors.bgPrimary};}
  .av{width:30px;height:30px;border-radius:50%;background:${theme.colors.gradient};display:flex;align-items:center;justify-content:center;color:#fff;font-size:.8rem;flex-shrink:0;position:relative;}
  .unc{min-width:18px;height:18px;padding:0 5px;border-radius:999px;background:#ef4444;color:#fff;font-size:.7rem;font-weight:800;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;}`;
const ChatHead = styled.div`display:flex;align-items:center;justify-content:space-between;gap:.5rem;padding:.5rem .7rem;
  border-bottom:1px solid ${theme.colors.border};color:${theme.colors.textPrimary};font-weight:700;font-size:.86rem;
  button{background:none;border:none;color:${theme.colors.textMuted};cursor:pointer;display:inline-flex;}`;
const ChatWrap = styled.div`padding:.2rem .7rem .7rem;overflow-y:auto;`;
const Empty = styled.p`color:${theme.colors.textMuted};font-size:.82rem;text-align:center;padding:1rem;`;

const readKey = (gid) => 'vg_grp_read_' + gid;
const getLastRead = (gid) => { try { return parseInt(localStorage.getItem(readKey(gid)) || '0', 10) || 0; } catch { return 0; } };
const setLastRead = (gid) => { try { localStorage.setItem(readKey(gid), String(Date.now())); } catch { /* */ } };
const msgTime = (m) => { try { const t = m && m.create_time; if (!t) return 0; if (typeof t === 'string') return Date.parse(t) || 0; if (t.seconds) return Number(t.seconds) * 1000; return 0; } catch { return 0; } };

export default function FriendGroupsDock({ mode = 'float', onCount }) {
  const { auth, isLoggedIn } = useAuth();
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [unread, setUnread] = useState({});           // { groupId: nb de messages non lus }
  const chanToGroup = useRef({});                       // { channelId: groupId }
  const activeRef = useRef(null); activeRef.current = active;
  const userClosedRef = useRef(false);   // l'user a ferme le dock a la main -> PAS d'auto-reouverture
  const myId = auth.session && auth.session.user_id;

  const load = useCallback(() => {
    if (!isLoggedIn || !auth.enabled) { setGroups([]); return; }
    auth.listMyGroups().then((g) => setGroups(Array.isArray(g) ? g : [])).catch(() => {});
  }, [auth, isLoggedIn]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!open) return undefined;
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [open, load]);

  // Rejoint les canaux de TOUS mes groupes + compte les non-lus (historique après mon « dernier lu »).
  useEffect(() => {
    if (!isLoggedIn || !auth.enabled || groups.length === 0) return undefined;
    let alive = true;
    (async () => {
      for (const g of groups) {
        try {
          const ch = await auth.joinChannel(g.id, 3);
          if (!ch || !alive) continue;
          chanToGroup.current[ch.id] = g.id;
          if (activeRef.current && activeRef.current.id === g.id) continue;   // chat ouvert → pas de non-lu
          const hist = await auth.listChatHistory(ch.id, 30);
          const lr = getLastRead(g.id);
          const n = (hist || []).filter((m) => msgTime(m) > lr && m.sender_id !== myId).length;
          if (alive) setUnread((u) => (n > 0 ? { ...u, [g.id]: n } : (u[g.id] ? (() => { const c = { ...u }; delete c[g.id]; return c; })() : u)));
        } catch { /* */ }
      }
    })();
    return () => { alive = false; };
  }, [auth, isLoggedIn, groups, myId]);

  // Messages LIVE → incrémente le non-lu du groupe (sauf mon propre message / chat déjà ouvert).
  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const off = auth.onChatMessage((m) => {
      const gid = chanToGroup.current[m && m.channel_id];
      if (!gid) return;
      if (m.sender_id === myId) return;
      if (activeRef.current && activeRef.current.id === gid) { setLastRead(gid); return; }
      setUnread((u) => ({ ...u, [gid]: (u[gid] || 0) + 1 }));
    });
    return off;
  }, [auth, isLoggedIn, myId]);

  // Un membre REJOINT/est actif dans un de mes groupes → on ouvre automatiquement sa discussion en bas.
  useEffect(() => {
    if (!isLoggedIn || !auth.onChannelPresence) return undefined;
    const off = auth.onChannelPresence((ev) => {
      const gid = chanToGroup.current[ev && ev.channel_id];
      if (!gid || !(ev.joins && ev.joins.length)) return;
      const others = ev.joins.filter((p) => p.user_id !== myId);
      if (others.length === 0) return;
      const g = groups.find((x) => x.id === gid);
      // Auto-ouverture SEULEMENT si l'user n'a pas ferme le dock a la main (sinon = badge non-lus).
      if (g && !userClosedRef.current && (!activeRef.current || activeRef.current.id !== gid)) { setActive(g); setOpen(true); }
    });
    return off;
  }, [auth, isLoggedIn, groups, myId]);

  const openGroup = (g) => { userClosedRef.current = false; setActive(g); setLastRead(g.id); setUnread((u) => { const c = { ...u }; delete c[g.id]; return c; }); };
  const groupsWithUnread = Object.keys(unread).filter((k) => unread[k] > 0).length;
  useEffect(() => { if (onCount) onCount(groups.length); }, [groups.length, onCount]);

  if (!isLoggedIn || groups.length === 0) return null;

  // MODE DRAWER (mobile) : section pleine largeur (liste des groupes + chat au clic).
  if (mode === 'drawer') {
    return (
      <Section>
        <SecHead><FiUsers /> Mes groupes <span style={{ color: theme.colors.primaryLight }}>({groups.length})</span></SecHead>
        {active ? (
          <>
            <ChatHead>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem' }}>
                <button onClick={() => setActive(null)} title="Retour"><FiArrowLeft /></button>💬 {active.name}
              </span>
            </ChatHead>
            <ChatWrap><GroupChat group={active} /></ChatWrap>
          </>
        ) : (
          <List>
            {groups.map((g) => (
              <GItem key={g.id} onClick={() => openGroup(g)}>
                <span className="av">{(g.name || '?').slice(0, 2).toUpperCase()}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</span>
                {unread[g.id] > 0 && <span className="unc" title="Messages non lus">{unread[g.id]}</span>}
                <span style={{ fontSize: '.72rem', color: theme.colors.textMuted }}>{g.edge_count || ''}</span>
              </GItem>
            ))}
          </List>
        )}
      </Section>
    );
  }

  return (
    <Dock>
      <Bar $open={open} onClick={() => { setOpen((o) => { userClosedRef.current = o; return !o; }); setActive(null); }}>
        <span className="n"><FiUsers /> Mes groupes <span className="b">{groups.length}</span></span>
        {open ? <FiChevronDown /> : <FiChevronUp />}
        {/* Fermée : badge = NOMBRE de groupes ayant des messages non lus (1, 2, 3…). */}
        {!open && groupsWithUnread > 0 && <span className="unb" title={`${groupsWithUnread} groupe(s) avec des messages non lus`}>{groupsWithUnread}</span>}
      </Bar>
      {open && (
        <Panel>
          {active ? (
            <>
              <ChatHead>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem' }}>
                  <button onClick={() => setActive(null)} title="Retour"><FiArrowLeft /></button>
                  💬 {active.name}
                </span>
                <button onClick={() => { setActive(null); setOpen(false); }} title="Fermer"><FiX /></button>
              </ChatHead>
              <ChatWrap><GroupChat group={active} /></ChatWrap>
            </>
          ) : (
            <List>
              {groups.length === 0 && <Empty>Aucun groupe. Crée-en un depuis ton espace membre.</Empty>}
              {groups.map((g) => (
                <GItem key={g.id} onClick={() => openGroup(g)}>
                  <span className="av">{(g.name || '?').slice(0, 2).toUpperCase()}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</span>
                  {unread[g.id] > 0 && <span className="unc" title="Messages non lus">{unread[g.id]}</span>}
                  <span style={{ fontSize: '.72rem', color: theme.colors.textMuted }}>{g.edge_count || ''}</span>
                </GItem>
              ))}
            </List>
          )}
        </Panel>
      )}
    </Dock>
  );
}
